import template from "./field_polygon_map.html";
import en from "./en.json";
import es from "./es.json";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

class FieldPolygonMapController {
    /* @ngInject */
    constructor($element, $scope, $q, $timeout) {
        this.$element = $element;
        this.$scope = $scope;
        this.$q = $q;
        this.$timeout = $timeout;
    }

    $onInit() {
        let ctrl = this;
        ctrl.path = [];
        ctrl.pathClosed = false;
        ctrl.pathMarkers = [];
        ctrl.pathMarkerListeners = [];
        ctrl.pathPolyline = null;
        ctrl.pathPolygon = null;
        ctrl.pointPositionsSignature = "";

        setOptions({
            key: process.env.GOOGLE_PUBLIC_API_KEY,
            v: "weekly",
            libraries: ["maps", "core", "marker"],
        });

        let mapsLibrary = importLibrary("maps");
        let markerLibrary = importLibrary("marker");
        let coreLibrary = importLibrary("core");

        ctrl.$q
            .all([mapsLibrary, markerLibrary, coreLibrary])
            .then(function (libs) {
                let [maps, marker, core] = libs;
                ctrl.maps = maps;
                ctrl.marker = marker;
                ctrl.core = core;

                ctrl.$timeout(function () {
                    ctrl.initializeMap();
                });
            });
    }

    initializeMap() {
        let ctrl = this;
        let mapElement = ctrl.$element.find("div")[0];
        if (!mapElement) {
            return;
        }

        ctrl.map = new ctrl.maps.Map(mapElement, {
            zoom: 15,
            mapTypeId: "hybrid",
            mapId: "DEMO_MAP_ID",
            cameraControl: false,
            zoomControl: true,
            mapTypeControl: true,
            fullscreenControl: false,
            streetViewControl: false,
            controlSize: 30,
        });

        ctrl.centerLatLngWatch = ctrl.$scope.$watchGroup(
            ["$ctrl.centerLat", "$ctrl.centerLng"],
            function (newValues, oldValues) {
                let [lat, lng] = newValues;

                if (lat === null || lat === undefined) {
                    return;
                }

                if (lng === null || lng === undefined) {
                    return;
                }

                if (oldValues && oldValues[0] === lat && oldValues[1] === lng) {
                    return;
                }

                ctrl.map.setCenter(
                    ctrl.normalizeLatLng({ lat: lat, lng: lng }),
                );
            },
        );

        ctrl.pointPositionsWatch = ctrl.$scope.$watchCollection(
            "$ctrl.pointPositions",
            function (newValue) {
                ctrl.syncPathFromInput(newValue || []);
            },
        );

        ctrl.mapClickListener = ctrl.map.addListener("click", function (e) {
            ctrl.handleMapClick(e.latLng);
        });

        ctrl.syncPathFromInput(ctrl.pointPositions || []);

        if (ctrl.path.length) {
            ctrl.map.setCenter(ctrl.path[0]);
            return;
        }

        if (ctrl.centerLat !== null && ctrl.centerLat !== undefined) {
            if (ctrl.centerLng !== null && ctrl.centerLng !== undefined) {
                ctrl.map.setCenter(
                    ctrl.normalizeLatLng({
                        lat: ctrl.centerLat,
                        lng: ctrl.centerLng,
                    }),
                );
            }
        }
    }

    syncPathFromInput(pointPositions) {
        let ctrl = this;
        let normalizedPoints = [];

        (pointPositions || []).forEach(function (position) {
            let normalized = ctrl.normalizeLatLng(position);
            if (normalized) {
                normalizedPoints.push(normalized);
            }
        });

        let pathClosed = false;
        if (normalizedPoints.length > 2) {
            let firstPoint = normalizedPoints[0];
            let lastPoint = normalizedPoints[normalizedPoints.length - 1];
            if (ctrl.getDistanceInMeters(firstPoint, lastPoint) <= 1) {
                pathClosed = true;
                normalizedPoints = normalizedPoints.slice(0, -1);
            }
        }

        let nextSignature = ctrl.getSignatureForPath(
            normalizedPoints,
            pathClosed,
        );
        if (nextSignature === ctrl.pointPositionsSignature) {
            return;
        }

        ctrl.path = normalizedPoints;
        ctrl.pathClosed = pathClosed;
        ctrl.pointPositionsSignature = nextSignature;
        ctrl.renderPath();
    }

    normalizeLatLng(position) {
        let ctrl = this;
        let coordinates = ctrl.getPositionCoordinates(position);
        if (!coordinates) {
            return null;
        }

        if (
            typeof position.lat === "function" &&
            typeof position.lng === "function"
        ) {
            return position;
        }

        return new ctrl.core.LatLng(coordinates.lat, coordinates.lng);
    }

    getPositionCoordinates(position) {
        if (position === null || position === undefined) {
            return null;
        }

        let lat;
        let lng;

        if (
            typeof position.lat === "function" &&
            typeof position.lng === "function"
        ) {
            lat = Number(position.lat());
            lng = Number(position.lng());
        } else {
            lat = Number(position.latitude);
            lng = Number(position.longitude);

            if (Number.isNaN(lat) || Number.isNaN(lng)) {
                lat = Number(position.lat);
                lng = Number(position.lng);
            }
        }

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
        }

        return {
            lat: lat,
            lng: lng,
        };
    }

    getDistanceInMeters(pointA, pointB) {
        let firstCoordinates = this.getPositionCoordinates(pointA);
        let secondCoordinates = this.getPositionCoordinates(pointB);
        if (!firstCoordinates || !secondCoordinates) {
            return Number.POSITIVE_INFINITY;
        }

        let toRadians = function (degrees) {
            return (degrees * Math.PI) / 180;
        };

        let earthRadiusMeters = 6371000;
        let latitudeDelta = toRadians(
            secondCoordinates.lat - firstCoordinates.lat,
        );
        let longitudeDelta = toRadians(
            secondCoordinates.lng - firstCoordinates.lng,
        );
        let a =
            Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
            Math.cos(toRadians(firstCoordinates.lat)) *
                Math.cos(toRadians(secondCoordinates.lat)) *
                Math.sin(longitudeDelta / 2) *
                Math.sin(longitudeDelta / 2);
        let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return earthRadiusMeters * c;
    }

    canCloseAtPosition(position) {
        let ctrl = this;
        if (ctrl.path.length < 3) {
            return false;
        }

        let firstPoint = ctrl.path[0];
        return ctrl.getDistanceInMeters(firstPoint, position) <= 20;
    }

    handleMapClick(position) {
        let ctrl = this;
        if (!position) {
            return;
        }

        if (!ctrl.pathClosed && ctrl.canCloseAtPosition(position)) {
            ctrl.pathClosed = true;
            ctrl.renderPath();
            ctrl.emitPointsUpdate();
            return;
        }

        if (ctrl.pathClosed) {
            return;
        }

        ctrl.path.push(position);
        ctrl.renderPath();
        ctrl.emitPointsUpdate();
    }

    serializePoint(position) {
        let coordinates = this.getPositionCoordinates(position);
        if (!coordinates) {
            return null;
        }

        return {
            latitude: Number(coordinates.lat.toFixed(8)),
            longitude: Number(coordinates.lng.toFixed(8)),
        };
    }

    getSignatureForPath(path, pathClosed) {
        let values = (path || []).map((position) => {
            let coordinates = this.getPositionCoordinates(position);
            if (!coordinates) {
                return "";
            }

            return [
                coordinates.lat.toFixed(8),
                coordinates.lng.toFixed(8),
            ].join(",");
        });
        return [values.join("|"), String(!!pathClosed)].join("::");
    }

    emitPointsUpdate() {
        let ctrl = this;
        let serializedPoints = ctrl.path
            .map(function (position) {
                return ctrl.serializePoint(position);
            })
            .filter(function (position) {
                return !!position;
            });

        if (ctrl.pathClosed && serializedPoints.length >= 3) {
            serializedPoints = serializedPoints.concat([serializedPoints[0]]);
        }

        ctrl.pointPositionsSignature = ctrl.getSignatureForPath(
            ctrl.path,
            ctrl.pathClosed,
        );

        ctrl.$scope.$evalAsync(function () {
            if (!ctrl.onPointsUpdate) {
                return;
            }

            ctrl.onPointsUpdate({
                points: serializedPoints,
                isClosed: ctrl.pathClosed,
            });
        });
    }

    canRemoveLastPoint() {
        let ctrl = this;
        return Array.isArray(ctrl.path) && ctrl.path.length > 0;
    }

    removeLastPoint() {
        let ctrl = this;
        if (!ctrl.canRemoveLastPoint()) {
            return;
        }

        ctrl.pathClosed = false;
        ctrl.path.pop();
        ctrl.renderPath();
        ctrl.emitPointsUpdate();
    }

    clearPathMarkers() {
        let ctrl = this;

        if (ctrl.pathMarkerListeners) {
            ctrl.pathMarkerListeners.forEach(function (listener) {
                listener.remove();
            });
            ctrl.pathMarkerListeners = [];
        }

        if (ctrl.pathMarkers) {
            ctrl.pathMarkers.forEach(function (pathMarker) {
                pathMarker.map = null;
            });
            ctrl.pathMarkers = [];
        }
    }

    createCircleMarkerElement() {
        let markerElement = document.createElement("div");
        markerElement.style.width = "12px";
        markerElement.style.height = "12px";
        markerElement.style.borderRadius = "9999px";
        markerElement.style.backgroundColor = "#57e140";
        markerElement.style.border = "1px solid #ffffff";
        markerElement.style.boxSizing = "border-box";
        return markerElement;
    }

    renderPath() {
        let ctrl = this;
        if (!ctrl.map) {
            return;
        }

        ctrl.clearPathMarkers();

        if (ctrl.pathPolyline) {
            ctrl.pathPolyline.setMap(null);
            ctrl.pathPolyline = null;
        }

        if (ctrl.pathPolygon) {
            ctrl.pathPolygon.setMap(null);
            ctrl.pathPolygon = null;
        }

        ctrl.path.forEach(function (position, index) {
            let pathMarker = new ctrl.marker.AdvancedMarkerElement({
                map: ctrl.map,
                position: position,
                gmpDraggable: true,
                content: ctrl.createCircleMarkerElement(),
                anchorLeft: "-50%",
                anchorTop: "-50%",
            });

            ctrl.pathMarkers.push(pathMarker);

            let updatePathPointFromMarker = function (event) {
                let markerPosition =
                    (event && event.latLng) || pathMarker.position;
                let normalizedPosition = ctrl.normalizeLatLng(markerPosition);
                if (!normalizedPosition) {
                    return;
                }

                ctrl.path[index] = normalizedPosition;
                ctrl.renderPath();
                ctrl.emitPointsUpdate();
            };

            ctrl.pathMarkerListeners.push(
                pathMarker.addListener("dragend", updatePathPointFromMarker),
            );

            if (typeof pathMarker.addEventListener === "function") {
                let dragEndListener = function (event) {
                    let detail = (event && event.detail) || {};
                    updatePathPointFromMarker(detail);
                };
                pathMarker.addEventListener("gmp-dragend", dragEndListener);
                ctrl.pathMarkerListeners.push({
                    remove: function () {
                        pathMarker.removeEventListener(
                            "gmp-dragend",
                            dragEndListener,
                        );
                    },
                });
            }

            if (index === 0 && ctrl.path.length >= 3 && !ctrl.pathClosed) {
                ctrl.pathMarkerListeners.push(
                    pathMarker.addListener("click", function () {
                        ctrl.pathClosed = true;
                        ctrl.renderPath();
                        ctrl.emitPointsUpdate();
                    }),
                );
            }
        });

        if (ctrl.path.length >= 2) {
            let polylinePath = ctrl.path;
            if (ctrl.pathClosed && ctrl.path.length >= 3) {
                polylinePath = ctrl.path.concat([ctrl.path[0]]);
            }

            ctrl.pathPolyline = new ctrl.maps.Polyline({
                map: ctrl.map,
                path: polylinePath,
                geodesic: true,
                strokeColor: "#57e140",
                strokeOpacity: 0.9,
                strokeWeight: 2,
            });
        }

        if (ctrl.pathClosed && ctrl.path.length >= 3) {
            ctrl.pathPolygon = new ctrl.maps.Polygon({
                map: ctrl.map,
                paths: ctrl.path,
                strokeColor: "#57e140",
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: "#57e140",
                fillOpacity: 0.2,
            });
        }
    }

    $onDestroy() {
        let ctrl = this;

        if (ctrl.mapClickListener) {
            ctrl.mapClickListener.remove();
            ctrl.mapClickListener = null;
        }

        if (ctrl.centerLatLngWatch) {
            ctrl.centerLatLngWatch();
            ctrl.centerLatLngWatch = null;
        }

        if (ctrl.pointPositionsWatch) {
            ctrl.pointPositionsWatch();
            ctrl.pointPositionsWatch = null;
        }

        ctrl.clearPathMarkers();

        if (ctrl.pathPolyline) {
            ctrl.pathPolyline.setMap(null);
            ctrl.pathPolyline = null;
        }

        if (ctrl.pathPolygon) {
            ctrl.pathPolygon.setMap(null);
            ctrl.pathPolygon = null;
        }
    }
}

export default {
    name: "fieldPolygonMap",
    template: template,
    controller: FieldPolygonMapController,
    bindings: {
        pointPositions: "<",
        centerLat: "<",
        centerLng: "<",
        heightClass: "@?",
        onPointsUpdate: "&?",
    },
    translations: { en, es },
};
